import { z } from 'zod';
/**
 * Embeds Service
 * Module 07 - Dashboard UI
 * 
 * Handles embed configuration operations
 */

import api from './api';
import { Embed, CreateEmbedInput, UpdateEmbedInput } from '../types';

const embedApiSchema = z.object({
  id: z.string().optional(),
  embedId: z.string().optional(),
  modelId: z.string().optional(),
  userId: z.string().optional(),
  name: z.string().optional(),
  title: z.string().optional(),
  settings: z.record(z.any()).optional(),
  allowedDomains: z.array(z.string()).optional(),
  allowed_domains: z.array(z.string()).optional(),
  isActive: z.boolean().optional(),
  active: z.boolean().optional(),
  viewCount: z.number().optional(),
  view_count: z.number().optional(),
  embedToken: z.string().optional(),
  token: z.string().optional(),
  embedCode: z.string().optional(),
  embed_code: z.string().optional(),
  embedCodeIframe: z.string().optional(),
  embed_code_iframe: z.string().optional(),
  posterImage: z.string().optional().nullable(),
  poster_image: z.string().optional().nullable(),
  createdAt: z.string().optional(),
  created_at: z.string().optional(),
  updatedAt: z.string().optional(),
  updated_at: z.string().optional(),
});

type RawEmbed = z.infer<typeof embedApiSchema>;

function normaliseEmbed(raw: RawEmbed): Embed {
  const embed = embedApiSchema.passthrough().parse(raw);

  return {
    id: embed.id || embed.embedId || embed.modelId || '',
    userId: embed.userId || '',
    modelId: embed.modelId || '',
    name: embed.name || embed.title || 'Untitled Embed',
    settings: (embed.settings as Embed['settings']) || ({} as Embed['settings']),
    allowedDomains: embed.allowedDomains || embed.allowed_domains || [],
    isActive: embed.isActive ?? embed.active ?? true,
    viewCount: embed.viewCount ?? embed.view_count ?? 0,
    embedToken: embed.embedToken || embed.token || '',
    embedCode: embed.embedCode || embed.embed_code || '',
    embedCodeIframe: embed.embedCodeIframe || embed.embed_code_iframe || '',
    posterImage: embed.posterImage ?? embed.poster_image ?? null,
    createdAt: embed.createdAt || embed.created_at || new Date().toISOString(),
    updatedAt: embed.updatedAt || embed.updated_at || embed.createdAt || new Date().toISOString(),
  };
}

export const embedsService = {
  /**
   * Get all embeds for current user
   * 
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Paginated list of embeds
   */
  async getEmbeds(page: number = 1, limit: number = 20): Promise<Embed[]> {
    const response = await api.get('/embeds', {
      params: { page, limit },
    });
    const payload = response.data;
    const rawEmbeds: unknown[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.embeds)
        ? payload.embeds
        : [];
    return rawEmbeds.map((embed) => normaliseEmbed(embed as RawEmbed));
  },

  /**
   * Get single embed by ID
   * 
   * @param id - Embed ID
   * @returns Embed details
   */
  async getEmbed(id: string): Promise<Embed> {
    const response = await api.get(`/embeds/${id}`);
    return normaliseEmbed(response.data as RawEmbed);
  },

  /**
   * Create new embed configuration
   * 
   * @param input - Embed creation data
   * @returns Created embed with token and code
   */
  async createEmbed(input: CreateEmbedInput): Promise<Embed> {
    const response = await api.post('/embeds', input);
    return {
      ...normaliseEmbed(response.data as RawEmbed),
      embedCode: response.data.embedCode || response.data.embed_code,
      embedCodeIframe: response.data.embedCodeIframe || response.data.embed_code_iframe,
    };
  },

  /**
   * Update existing embed
   * 
   * @param id - Embed ID
   * @param input - Fields to update
   * @returns Updated embed
   */
  async updateEmbed(id: string, input: UpdateEmbedInput): Promise<Embed> {
    const response = await api.patch(`/embeds/${id}`, input);
    return normaliseEmbed(response.data as RawEmbed);
  },

  /**
   * Delete embed by ID
   * 
   * @param id - Embed ID to delete
   */
  async deleteEmbed(id: string): Promise<void> {
    await api.delete(`/embeds/${id}`);
  },

  /**
   * Toggle embed active status
   * 
   * @param id - Embed ID
   * @param isActive - New active status
   * @returns Updated embed
   */
  async toggleEmbedStatus(id: string, isActive: boolean): Promise<Embed> {
    return this.updateEmbed(id, { isActive });
  },

  /**
   * Regenerate embed token (for security)
   * 
   * @param id - Embed ID
   * @returns Embed with new token
   */
  async regenerateToken(id: string): Promise<Embed> {
    const response = await api.post(`/embeds/${id}/regenerate-token`);
    return normaliseEmbed(response.data as RawEmbed);
  },

  /**
   * Reset view count for an embed
   *
   * @param id - Embed ID
   */
  async resetViewCount(id: string): Promise<void> {
    await api.post(`/embeds/${id}/reset-views`);
  },

  /**
   * Get embed analytics/stats
   *
   * @param id - Embed ID
   * @returns Embed analytics data
   */
  async getEmbedAnalytics(id: string): Promise<{
    viewCount: number;
    lastViewed: string | null;
    averageLoadTime: number;
    errorCount: number;
  }> {
    const response = await api.get(`/embeds/${id}/analytics`);
    return response.data;
  },

  /**
   * Test embed configuration
   * Fetches embed config as viewer would (without consuming view count)
   * 
   * @param id - Embed ID
   * @returns Embed configuration
   */
  async testEmbedConfig(id: string): Promise<any> {
    const response = await api.get(`/embeds/${id}/test-config`);
    return response.data;
  },

  /**
   * Copy embed code to clipboard
   * 
   * @param embedCode - Embed HTML code
   * @returns True if successful
   */
  async copyEmbedCode(embedCode: string): Promise<boolean> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(embedCode);
        return true;
      }
      
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = embedCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      return true;
    } catch (error) {
      console.error('Failed to copy embed code:', error);
      return false;
    }
  },
};
